import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  TrendingUp, TrendingDown, BarChart3, PieChart,
  ChevronDown, ChevronUp, Calendar, DollarSign,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category colors
const CATEGORY_COLORS = {
  software: '#3b82f6',
  insurance: '#dc2626',
  design: '#8b5cf6',
  marketing: '#10b981',
  legal: '#f59e0b',
  hosting: '#0891b2',
  tools: '#7c3aed',
  other: '#6b7280'
};

const CATEGORY_LABELS = {
  software: 'Software/AI',
  insurance: 'Insurance',
  design: 'Design',
  marketing: 'Marketing',
  legal: 'Legal/Admin',
  hosting: 'Hosting/Domain',
  tools: 'Tools/Services',
  other: 'Other'
};

const ExpenseTrends = () => {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [trendsData, setTrendsData] = useState(null);
  const [activeView, setActiveView] = useState('bar'); // 'bar' or 'category'

  const fetchTrends = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/admin/expenses/trends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrendsData(data);
      }
    } catch (err) {
      console.error('Failed to fetch expense trends:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTrends();
  }, [fetchTrends]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  // Calculate max value for bar scaling
  const maxMonthlyTotal = trendsData?.monthly_trends?.length > 0
    ? Math.max(...trendsData.monthly_trends.map(m => m.total))
    : 0;

  // Calculate total for category percentages
  const categoryTotal = trendsData?.category_totals
    ? Object.values(trendsData.category_totals).reduce((a, b) => a + b, 0)
    : 0;

  // Get current and previous month for comparison
  const currentMonth = trendsData?.monthly_trends?.slice(-1)[0];
  const previousMonth = trendsData?.monthly_trends?.slice(-2, -1)[0];

  return (
    <div className="expense-trends-section" data-testid="expense-trends">
      <div 
        className="trends-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="trends-title">
          <BarChart3 size={20} />
          <h3>Expense Trends & Analytics</h3>
        </div>
        <button className="expand-btn">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      {expanded && (
        <div className="trends-content">
          {loading ? (
            <div className="trends-loading">
              <div className="loading-spinner"></div>
              <span>Loading trends...</span>
            </div>
          ) : trendsData ? (
            <>
              {/* Month-over-Month Comparison Cards */}
              <div className="comparison-cards">
                <div className="comparison-card current">
                  <div className="comparison-label">
                    <Calendar size={14} />
                    <span>Current Month</span>
                  </div>
                  <div className="comparison-value">
                    {formatCurrency(currentMonth?.total || 0)}
                  </div>
                  <div className="comparison-meta">
                    {currentMonth?.count || 0} expenses
                  </div>
                </div>

                <div className="comparison-card previous">
                  <div className="comparison-label">
                    <Calendar size={14} />
                    <span>Previous Month</span>
                  </div>
                  <div className="comparison-value">
                    {formatCurrency(previousMonth?.total || 0)}
                  </div>
                  <div className="comparison-meta">
                    {previousMonth?.count || 0} expenses
                  </div>
                </div>

                <div className="comparison-card change">
                  <div className="comparison-label">
                    <TrendingUp size={14} />
                    <span>Change</span>
                  </div>
                  <div className={`comparison-value ${
                    (currentMonth?.change_pct || 0) > 0 ? 'increase' : 
                    (currentMonth?.change_pct || 0) < 0 ? 'decrease' : ''
                  }`}>
                    {(currentMonth?.change_pct || 0) > 0 ? (
                      <ArrowUpRight size={18} />
                    ) : (currentMonth?.change_pct || 0) < 0 ? (
                      <ArrowDownRight size={18} />
                    ) : (
                      <Minus size={18} />
                    )}
                    {Math.abs(currentMonth?.change_pct || 0)}%
                  </div>
                  <div className="comparison-meta">
                    month-over-month
                  </div>
                </div>
              </div>

              {/* View Toggle */}
              <div className="view-toggle">
                <button 
                  className={`toggle-btn ${activeView === 'bar' ? 'active' : ''}`}
                  onClick={() => setActiveView('bar')}
                >
                  <BarChart3 size={16} />
                  Monthly Trend
                </button>
                <button 
                  className={`toggle-btn ${activeView === 'category' ? 'active' : ''}`}
                  onClick={() => setActiveView('category')}
                >
                  <PieChart size={16} />
                  By Category
                </button>
              </div>

              {/* Bar Chart - Monthly Trends */}
              {activeView === 'bar' && trendsData.monthly_trends?.length > 0 && (
                <div className="bar-chart-section">
                  <h4>Monthly Expense Trend</h4>
                  <div className="bar-chart">
                    {trendsData.monthly_trends.map((month, index) => {
                      const heightPct = maxMonthlyTotal > 0 
                        ? (month.total / maxMonthlyTotal) * 100 
                        : 0;
                      
                      return (
                        <div key={month.month} className="bar-item">
                          <div className="bar-value">{formatCurrency(month.total)}</div>
                          <div className="bar-container">
                            <div 
                              className="bar-fill"
                              style={{ height: `${heightPct}%` }}
                              title={`${month.month_label}: ${formatCurrency(month.total)}`}
                            >
                              {month.change_pct !== 0 && index > 0 && (
                                <span className={`bar-change ${month.change_pct > 0 ? 'up' : 'down'}`}>
                                  {month.change_pct > 0 ? '+' : ''}{month.change_pct}%
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="bar-label">{month.month_label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Category Breakdown */}
              {activeView === 'category' && (
                <div className="category-chart-section">
                  <h4>Spending by Category</h4>
                  
                  {/* Horizontal bars */}
                  <div className="category-bars">
                    {Object.entries(trendsData.category_totals || {})
                      .sort((a, b) => b[1] - a[1])
                      .map(([category, amount]) => {
                        const pct = categoryTotal > 0 ? (amount / categoryTotal) * 100 : 0;
                        const color = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
                        const label = CATEGORY_LABELS[category] || category;
                        
                        return (
                          <div key={category} className="category-bar-row">
                            <div className="category-info">
                              <span 
                                className="category-dot" 
                                style={{ backgroundColor: color }}
                              />
                              <span className="category-name">{label}</span>
                            </div>
                            <div className="category-bar-wrapper">
                              <div 
                                className="category-bar-fill"
                                style={{ 
                                  width: `${pct}%`,
                                  backgroundColor: color
                                }}
                              />
                            </div>
                            <div className="category-stats">
                              <span className="category-amount">{formatCurrency(amount)}</span>
                              <span className="category-pct">{pct.toFixed(1)}%</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {/* Recurring vs One-time */}
                  <div className="recurring-breakdown">
                    <h5>Recurring vs One-Time</h5>
                    <div className="recurring-bars">
                      <div className="recurring-item">
                        <div className="recurring-label">
                          <DollarSign size={14} />
                          One-Time
                        </div>
                        <div className="recurring-bar-wrapper">
                          <div 
                            className="recurring-bar-fill one-time"
                            style={{ 
                              width: `${categoryTotal > 0 
                                ? (trendsData.recurring_breakdown?.one_time / categoryTotal) * 100 
                                : 0}%` 
                            }}
                          />
                        </div>
                        <span className="recurring-amount">
                          {formatCurrency(trendsData.recurring_breakdown?.one_time || 0)}
                        </span>
                      </div>
                      <div className="recurring-item">
                        <div className="recurring-label">
                          <TrendingUp size={14} />
                          Recurring
                        </div>
                        <div className="recurring-bar-wrapper">
                          <div 
                            className="recurring-bar-fill recurring"
                            style={{ 
                              width: `${categoryTotal > 0 
                                ? (trendsData.recurring_breakdown?.recurring / categoryTotal) * 100 
                                : 0}%` 
                            }}
                          />
                        </div>
                        <span className="recurring-amount">
                          {formatCurrency(trendsData.recurring_breakdown?.recurring || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* No data message */}
              {(!trendsData.monthly_trends || trendsData.monthly_trends.length === 0) && (
                <div className="no-trends-data">
                  <BarChart3 size={48} />
                  <p>No expense data yet</p>
                  <span>Add expenses to see trends and analytics</span>
                </div>
              )}
            </>
          ) : (
            <div className="trends-error">
              <p>Unable to load trends data</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseTrends;
